import { exportPAL, importPAL, importBMP, importPCX, downloadBlob } from '../util/io.js';

export class PaletteEditDialog {
    constructor(doc, bus, undoManager) {
        this.doc = doc;
        this.bus = bus;
        this.undoManager = undoManager;
        this.onClose = null;
        this.onPick = null; // callback(colorIndex) — pick mode

        this._overlay = null;
        this._onKey = null;
        this._dlgSwatches = [];
        this._rangeStart = doc.fgColorIndex;
        this._rangeEnd = doc.fgColorIndex;
        this._dragging = false;
        this._6bit = true;
        this._pendingOp = null;
        this._originalPalette = null;
        this._originalLayers = null;
        this._statusEl = null;
        this._usedHighlight = false;
        this._paletteHistory = [];
        this._undoBtn = null;
        this._sliderDirty = false;

        // HSV picker state
        this._hue = 0;        // 0-360
        this._sat = 0;        // 0-1
        this._val = 1;        // 0-1
        this._svCanvas = null;
        this._svCtx = null;
        this._hueCanvas = null;
        this._hueCtx = null;
        this._svCursor = null;
        this._hueCursor = null;
        this._svDragging = false;
        this._hueDragging = false;

        this._rSlider = null; this._gSlider = null; this._bSlider = null;
        this._rNum = null; this._gNum = null; this._bNum = null; this._hexInput = null;
        this._indexLabel = null;
        this._rangePreview = null;
        this._grid = null;
    }

    setInitialIndex(idx) {
        this._rangeStart = idx;
        this._rangeEnd = idx;
    }

    open() {
        this._originalPalette = this.doc.palette.export();
        if (this.doc.animationEnabled) this.doc.saveCurrentFrame();
        this._originalLayers = this.doc.layers.map(l => l.clone(true));
        this._originalAnim = this.doc.snapshotAnimation();
        this._buildDOM();
        document.body.appendChild(this._overlay);
    }

    // ── DOM Construction ──────────────────────────────────────────────

    _buildDOM() {
        const overlay = document.createElement('div');
        overlay.className = 'palette-dialog-overlay';
        this._overlay = overlay;

        const dialog = document.createElement('div');
        dialog.className = 'palette-dialog';

        // Header
        const header = document.createElement('div');
        header.className = 'palette-dialog-header';
        header.innerHTML = '<span>Edit Palette</span>';
        const closeBtn = document.createElement('button');
        closeBtn.className = 'palette-dialog-close';
        closeBtn.textContent = '\u00D7';
        closeBtn.addEventListener('click', () => this._cancel());
        header.appendChild(closeBtn);
        dialog.appendChild(header);

        // Toolbar (full width)
        dialog.appendChild(this._buildToolbar());

        // 6-bit checkbox
        const checkRow = document.createElement('div');
        checkRow.className = 'palette-dialog-6bit';
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.id = 'palette-6bit-check';
        checkbox.checked = this._6bit;
        checkbox.addEventListener('change', () => this._on6bitToggle(checkbox));
        const checkLabel = document.createElement('label');
        checkLabel.htmlFor = 'palette-6bit-check';
        checkLabel.textContent = '6 bit/channel';
        checkRow.appendChild(checkbox);
        checkRow.appendChild(checkLabel);
        dialog.appendChild(checkRow);

        // Status message (for two-step ops)
        const status = document.createElement('div');
        status.className = 'palette-dialog-status';
        status.style.display = 'none';
        this._statusEl = status;
        dialog.appendChild(status);

        // Index label
        const indexLabel = document.createElement('div');
        indexLabel.className = 'palette-dialog-index';
        this._indexLabel = indexLabel;
        dialog.appendChild(indexLabel);

        // Grid row: grid (left) + sliders (right)
        const gridRow = document.createElement('div');
        gridRow.className = 'ped-grid-row';

        const grid = document.createElement('div');
        grid.className = 'palette-dialog-grid';
        this._grid = grid;
        this._dlgSwatches = [];

        for (let i = 0; i < 256; i++) {
            const sw = document.createElement('div');
            sw.className = 'palette-dialog-swatch';
            sw.dataset.index = i;
            const [r, g, b] = this.doc.palette.getColor(i);
            sw.style.backgroundColor = `rgb(${r},${g},${b})`;
            grid.appendChild(sw);
            this._dlgSwatches.push(sw);
        }

        grid.addEventListener('pointerdown', (e) => this._onGridPointerDown(e));
        grid.addEventListener('pointermove', (e) => this._onGridPointerMove(e));
        grid.addEventListener('pointerup', (e) => this._onGridPointerUp(e));

        // Grid column: grid + range preview stacked
        const gridCol = document.createElement('div');
        gridCol.className = 'ped-grid-col';
        gridCol.appendChild(grid);

        const rangePreview = document.createElement('div');
        rangePreview.className = 'palette-dialog-range-preview';
        this._rangePreview = rangePreview;
        gridCol.appendChild(rangePreview);

        gridRow.appendChild(gridCol);
        gridRow.appendChild(this._buildColorPicker());
        dialog.appendChild(gridRow);

        // Footer
        const footer = document.createElement('div');
        footer.className = 'palette-dialog-footer';

        const leftBtns = document.createElement('div');
        leftBtns.className = 'palette-dialog-footer-btns';

        const undoBtn = document.createElement('button');
        undoBtn.textContent = 'Undo';
        undoBtn.disabled = true;
        undoBtn.addEventListener('click', () => this._undoPalette());
        this._undoBtn = undoBtn;
        leftBtns.appendChild(undoBtn);

        const loadBtn = document.createElement('button');
        loadBtn.textContent = 'Load';
        loadBtn.addEventListener('click', () => this._loadPalette());
        leftBtns.appendChild(loadBtn);

        const saveBtn = document.createElement('button');
        saveBtn.textContent = 'Save';
        saveBtn.addEventListener('click', () => this._savePalette());
        leftBtns.appendChild(saveBtn);

        footer.appendChild(leftBtns);

        const rightBtns = document.createElement('div');
        rightBtns.className = 'palette-dialog-footer-btns';
        const cancelBtn = document.createElement('button');
        cancelBtn.textContent = 'Cancel';
        cancelBtn.addEventListener('click', () => this._cancel());
        const okBtn = document.createElement('button');
        okBtn.textContent = 'OK';
        okBtn.className = 'primary';
        okBtn.addEventListener('click', () => this._ok());
        rightBtns.appendChild(cancelBtn);
        rightBtns.appendChild(okBtn);
        footer.appendChild(rightBtns);
        dialog.appendChild(footer);

        overlay.appendChild(dialog);

        let overlayMouseDown = false;
        overlay.addEventListener('mousedown', (e) => {
            overlayMouseDown = e.target === overlay;
        });
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay && overlayMouseDown) this._cancel();
            overlayMouseDown = false;
        });

        this._onKey = (e) => {
            if (e.key === 'Escape') {
                if (this._pendingOp) {
                    this._cancelPendingOp();
                } else {
                    this._cancel();
                }
                return;
            }
            // Ctrl+Z inside the dialog routes to the dialog's own palette undo,
            // never the global undo stack. Skip when a text field is focused so
            // it keeps its native text-undo.
            if (e.ctrlKey && !e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
                const t = e.target.tagName;
                if (t === 'INPUT' || t === 'TEXTAREA') return;
                e.preventDefault();
                this._undoPalette();
            }
        };
        document.addEventListener('keydown', this._onKey);

        this._updateRangeHighlight();
        this._updateRangePreview();
        this._syncPicker();
    }

    _buildToolbar() {
        const toolbar = document.createElement('div');
        toolbar.className = 'palette-dialog-toolbar';

        const btn = (label, action) => {
            const b = document.createElement('button');
            b.textContent = label;
            b.addEventListener('click', action);
            toolbar.appendChild(b);
            return b;
        };

        this._swapBtn = btn('Swap', () => this._startPendingOp('swap'));
        this._xswapBtn = btn('X-Swap', () => this._startPendingOp('xswap'));
        btn('Copy', () => this._actionCopyRange());
        this._pasteRangeBtn = btn('Paste', () => this._actionPasteRange());
        this._pasteRangeBtn.disabled = !PaletteEditDialog._rangeClipboard;
        this._flipBtn = btn('Flip', () => this._actionFlip());
        this._xflipBtn = btn('X-Flip', () => this._actionXFlip());

        // Sort select
        const sortSel = document.createElement('select');
        sortSel.className = 'palette-dialog-toolbar-select';
        this._sortSel = sortSel;
        for (const [val, label] of [['', 'Sort...'], ['hue', 'Hue/Light'], ['light', 'Lightness'], ['hist', 'Histogram']]) {
            const opt = document.createElement('option');
            opt.value = val;
            opt.textContent = label;
            sortSel.appendChild(opt);
        }
        sortSel.addEventListener('change', () => {
            if (sortSel.value) {
                this._actionSort(sortSel.value);
                sortSel.value = '';
            }
        });
        toolbar.appendChild(sortSel);

        btn('Used', () => this._actionUsed());
        this._invertBtn = btn('Invert', () => this._actionInvert());
        this._grayBtn = btn('Gray', () => this._actionGray());
        this._spreadBtn = btn('Spread', () => this._actionSpread());
        this._mergeBtn = btn('Merge', () => this._actionMerge());
        btn('Zap', () => this._actionZapUnused());

        // Separator
        const sep = document.createElement('div');
        sep.className = 'ped-toolbar-sep';
        toolbar.appendChild(sep);

        // Reduce row
        const reduceRow = document.createElement('div');
        reduceRow.className = 'ped-reduce-row';
        const reduceBtn = document.createElement('button');
        reduceBtn.textContent = 'Reduce to';
        const reduceNum = document.createElement('input');
        reduceNum.type = 'number';
        reduceNum.min = 1;
        reduceNum.max = 256;
        reduceNum.value = 16;
        reduceNum.className = 'palette-dialog-toolbar-num';
        reduceBtn.addEventListener('click', () => this._actionReduce(parseInt(reduceNum.value) || 16));
        reduceRow.appendChild(reduceBtn);
        reduceRow.appendChild(reduceNum);
        toolbar.appendChild(reduceRow);

        return toolbar;
    }

    _buildColorPicker() {
        const container = document.createElement('div');
        container.className = 'ped-color-picker';

        // Top row: SV canvas + hue strip
        const pickerRow = document.createElement('div');
        pickerRow.className = 'ped-picker-row';

        // SV area
        const svArea = document.createElement('div');
        svArea.className = 'ped-sv-area';

        const svCanvas = document.createElement('canvas');
        svCanvas.className = 'ped-sv-canvas';
        svCanvas.width = 160;
        svCanvas.height = 160;
        this._svCanvas = svCanvas;
        this._svCtx = svCanvas.getContext('2d');
        svArea.appendChild(svCanvas);

        const svCursor = document.createElement('div');
        svCursor.className = 'ped-sv-cursor';
        this._svCursor = svCursor;
        svArea.appendChild(svCursor);

        svCanvas.addEventListener('pointerdown', (e) => this._onSVPointerDown(e));
        svCanvas.addEventListener('pointermove', (e) => this._onSVPointerMove(e));
        svCanvas.addEventListener('pointerup', (e) => this._onSVPointerUp(e));

        pickerRow.appendChild(svArea);

        // Hue strip
        const hueStrip = document.createElement('div');
        hueStrip.className = 'ped-hue-strip';

        const hueCanvas = document.createElement('canvas');
        hueCanvas.className = 'ped-hue-canvas';
        hueCanvas.width = 20;
        hueCanvas.height = 160;
        this._hueCanvas = hueCanvas;
        this._hueCtx = hueCanvas.getContext('2d');
        hueStrip.appendChild(hueCanvas);

        const hueCursor = document.createElement('div');
        hueCursor.className = 'ped-hue-cursor';
        this._hueCursor = hueCursor;
        hueStrip.appendChild(hueCursor);

        hueCanvas.addEventListener('pointerdown', (e) => this._onHuePointerDown(e));
        hueCanvas.addEventListener('pointermove', (e) => this._onHuePointerMove(e));
        hueCanvas.addEventListener('pointerup', (e) => this._onHuePointerUp(e));

        pickerRow.appendChild(hueStrip);
        container.appendChild(pickerRow);

        // RGB slider rows
        const max = this._6bit ? 63 : 255;
        const makeSliderRow = (label) => {
            const row = document.createElement('div');
            row.className = 'ped-inputs-row';

            const lbl = document.createElement('div');
            lbl.className = 'ped-rgb-label';
            lbl.textContent = label;
            row.appendChild(lbl);

            const slider = document.createElement('input');
            slider.type = 'range';
            slider.min = 0;
            slider.max = max;
            slider.value = 0;
            slider.className = 'ped-rgb-slider';
            row.appendChild(slider);

            const num = document.createElement('input');
            num.type = 'number';
            num.min = 0;
            num.max = max;
            num.value = 0;
            num.className = 'ped-rgb-num';
            row.appendChild(num);

            container.appendChild(row);
            return { slider, num };
        };

        const rRow = makeSliderRow('R');
        const gRow = makeSliderRow('G');
        const bRow = makeSliderRow('B');

        this._rSlider = rRow.slider; this._rNum = rRow.num;
        this._gSlider = gRow.slider; this._gNum = gRow.num;
        this._bSlider = bRow.slider; this._bNum = bRow.num;

        const applyFromSliders = () => {
            this._rNum.value = this._rSlider.value;
            this._gNum.value = this._gSlider.value;
            this._bNum.value = this._bSlider.value;
            this._applyFromRGBInputs();
        };

        for (const s of [this._rSlider, this._gSlider, this._bSlider]) {
            s.addEventListener('input', applyFromSliders);
        }
        for (const n of [this._rNum, this._gNum, this._bNum]) {
            n.addEventListener('change', () => this._applyFromRGBInputs());
        }

        // Hex row
        const hexRow = document.createElement('div');
        hexRow.className = 'ped-inputs-row';

        const hexLabel = document.createElement('div');
        hexLabel.className = 'ped-rgb-label';
        hexLabel.textContent = '#';
        hexRow.appendChild(hexLabel);

        const hexInput = document.createElement('input');
        hexInput.type = 'text';
        hexInput.maxLength = 6;
        hexInput.className = 'ped-hex-input';
        hexInput.value = '000000';
        this._hexInput = hexInput;
        hexRow.appendChild(hexInput);

        hexInput.addEventListener('paste', (e) => {
            e.preventDefault();
            const text = (e.clipboardData || window.clipboardData).getData('text');
            hexInput.value = text.replace(/^#/, '').slice(0, 6);
            hexInput.dispatchEvent(new Event('change'));
        });

        hexInput.addEventListener('change', () => this._applyFromHexInput());

        container.appendChild(hexRow);

        return container;
    }

    // ── HSV Conversion ───────────────────────────────────────────────

    _rgbToHsv(r, g, b) {
        r /= 255; g /= 255; b /= 255;
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        const d = max - min;
        const v = max;
        const s = max === 0 ? 0 : d / max;
        let h = 0;
        if (d !== 0) {
            if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
            else if (max === g) h = ((b - r) / d + 2) * 60;
            else h = ((r - g) / d + 4) * 60;
        }
        return [h, s, v];
    }

    _hsvToRgb(h, s, v) {
        h = ((h % 360) + 360) % 360;
        const c = v * s;
        const x = c * (1 - Math.abs((h / 60) % 2 - 1));
        const m = v - c;
        let r, g, b;
        if (h < 60)       { r = c; g = x; b = 0; }
        else if (h < 120) { r = x; g = c; b = 0; }
        else if (h < 180) { r = 0; g = c; b = x; }
        else if (h < 240) { r = 0; g = x; b = c; }
        else if (h < 300) { r = x; g = 0; b = c; }
        else               { r = c; g = 0; b = x; }
        return [
            Math.round((r + m) * 255),
            Math.round((g + m) * 255),
            Math.round((b + m) * 255)
        ];
    }

    // ── Color Picker Rendering ───────────────────────────────────────

    _renderSVCanvas() {
        const ctx = this._svCtx;
        const w = this._svCanvas.width;
        const h = this._svCanvas.height;
        const [r, g, b] = this._hsvToRgb(this._hue, 1, 1);
        ctx.fillStyle = `rgb(${r},${g},${b})`;
        ctx.fillRect(0, 0, w, h);
        const white = ctx.createLinearGradient(0, 0, w, 0);
        white.addColorStop(0, 'rgba(255,255,255,1)');
        white.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = white;
        ctx.fillRect(0, 0, w, h);
        const black = ctx.createLinearGradient(0, 0, 0, h);
        black.addColorStop(0, 'rgba(0,0,0,0)');
        black.addColorStop(1, 'rgba(0,0,0,1)');
        ctx.fillStyle = black;
        ctx.fillRect(0, 0, w, h);
    }

    _renderHueStrip() {
        const ctx = this._hueCtx;
        const w = this._hueCanvas.width;
        const h = this._hueCanvas.height;
        const grad = ctx.createLinearGradient(0, 0, 0, h);
        for (const deg of [0, 60, 120, 180, 240, 300, 360]) {
            const [r, g, b] = this._hsvToRgb(deg, 1, 1);
            grad.addColorStop(deg / 360, `rgb(${r},${g},${b})`);
        }
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
    }

    _updateSVCursor() {
        const w = this._svCanvas.width;
        const h = this._svCanvas.height;
        this._svCursor.style.left = (this._sat * w) + 'px';
        this._svCursor.style.top = ((1 - this._val) * h) + 'px';
    }

    _updateHueCursor() {
        const h = this._hueCanvas.height;
        this._hueCursor.style.top = (this._hue / 360 * h) + 'px';
    }

    // ── SV Canvas Interaction ────────────────────────────────────────

    _onSVPointerDown(e) {
        if (e.button !== 0) return;
        this._svDragging = true;
        e.target.setPointerCapture(e.pointerId);
        this._updateSVFromPointer(e);
    }

    _onSVPointerMove(e) {
        if (!this._svDragging) return;
        this._updateSVFromPointer(e);
    }

    _onSVPointerUp(e) {
        if (!this._svDragging) return;
        this._svDragging = false;
        e.target.releasePointerCapture(e.pointerId);
    }

    _updateSVFromPointer(e) {
        const rect = this._svCanvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        this._sat = Math.max(0, Math.min(1, x / rect.width));
        this._val = Math.max(0, Math.min(1, 1 - y / rect.height));
        this._updateSVCursor();
        this._applyPickerColor();
    }

    // ── Hue Strip Interaction ────────────────────────────────────────

    _onHuePointerDown(e) {
        if (e.button !== 0) return;
        this._hueDragging = true;
        e.target.setPointerCapture(e.pointerId);
        this._updateHueFromPointer(e);
    }

    _onHuePointerMove(e) {
        if (!this._hueDragging) return;
        this._updateHueFromPointer(e);
    }

    _onHuePointerUp(e) {
        if (!this._hueDragging) return;
        this._hueDragging = false;
        e.target.releasePointerCapture(e.pointerId);
    }

    _updateHueFromPointer(e) {
        const rect = this._hueCanvas.getBoundingClientRect();
        const y = e.clientY - rect.top;
        this._hue = Math.max(0, Math.min(360, y / rect.height * 360));
        this._renderSVCanvas();
        this._updateHueCursor();
        this._applyPickerColor();
    }

    // ── Apply Color ──────────────────────────────────────────────────

    _rgbToHex(r, g, b) {
        return ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1).toUpperCase();
    }

    _syncRGBDisplay(r, g, b) {
        if (this._6bit) {
            const rv = Math.round(r / 4), gv = Math.round(g / 4), bv = Math.round(b / 4);
            this._rSlider.value = rv; this._rNum.value = rv;
            this._gSlider.value = gv; this._gNum.value = gv;
            this._bSlider.value = bv; this._bNum.value = bv;
        } else {
            this._rSlider.value = r; this._rNum.value = r;
            this._gSlider.value = g; this._gNum.value = g;
            this._bSlider.value = b; this._bNum.value = b;
        }
        this._hexInput.value = this._rgbToHex(r, g, b);
    }

    _applyPickerColor() {
        if (!this._sliderDirty) {
            this._pushPaletteHistory();
            this._sliderDirty = true;
        }
        let [r, g, b] = this._hsvToRgb(this._hue, this._sat, this._val);
        [r, g, b] = this._snapIf6bit(r, g, b);
        const idx = this._rangeStart;
        this.doc.palette.setColor(idx, r, g, b);
        this._dlgSwatches[idx].style.backgroundColor = `rgb(${r},${g},${b})`;
        this._syncRGBDisplay(r, g, b);
        this._updateRangePreview();
        this.bus.emit('palette-changed');
    }

    _applyFromRGBInputs() {
        const max = this._6bit ? 63 : 255;
        const rv = Math.min(max, Math.max(0, parseInt(this._rNum.value) || 0));
        const gv = Math.min(max, Math.max(0, parseInt(this._gNum.value) || 0));
        const bv = Math.min(max, Math.max(0, parseInt(this._bNum.value) || 0));
        if (!this._sliderDirty) {
            this._pushPaletteHistory();
            this._sliderDirty = true;
        }
        const r = this._6bit ? rv * 4 : rv;
        const g = this._6bit ? gv * 4 : gv;
        const b = this._6bit ? bv * 4 : bv;
        const idx = this._rangeStart;
        this.doc.palette.setColor(idx, r, g, b);
        this._dlgSwatches[idx].style.backgroundColor = `rgb(${r},${g},${b})`;
        const [h, s, v] = this._rgbToHsv(r, g, b);
        if (s > 0) this._hue = h;
        this._sat = s;
        this._val = v;
        this._syncRGBDisplay(r, g, b);
        this._renderSVCanvas();
        this._updateSVCursor();
        this._updateHueCursor();
        this._updateRangePreview();
        this.bus.emit('palette-changed');
    }

    _applyFromHexInput() {
        const hex = this._hexInput.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
        if (hex.length !== 6) return;
        let r = parseInt(hex.slice(0, 2), 16);
        let g = parseInt(hex.slice(2, 4), 16);
        let b = parseInt(hex.slice(4, 6), 16);
        if (!this._sliderDirty) {
            this._pushPaletteHistory();
            this._sliderDirty = true;
        }
        [r, g, b] = this._snapIf6bit(r, g, b);
        const idx = this._rangeStart;
        this.doc.palette.setColor(idx, r, g, b);
        this._dlgSwatches[idx].style.backgroundColor = `rgb(${r},${g},${b})`;
        const [h, s, v] = this._rgbToHsv(r, g, b);
        if (s > 0) this._hue = h;
        this._sat = s;
        this._val = v;
        this._syncRGBDisplay(r, g, b);
        this._renderSVCanvas();
        this._updateSVCursor();
        this._updateHueCursor();
        this._updateRangePreview();
        this.bus.emit('palette-changed');
    }

    // ── Grid Pointer Events (Range Selection) ─────────────────────────

    _hitTestGrid(e) {
        const rect = this._grid.getBoundingClientRect();
        const col = Math.floor((e.clientX - rect.left) / (rect.width / 16));
        const row = Math.floor((e.clientY - rect.top) / (rect.height / 16));
        return Math.max(0, Math.min(255, row * 16 + Math.max(0, Math.min(15, col))));
    }

    _onGridPointerDown(e) {
        if (e.button !== 0) return;
        const idx = this._hitTestGrid(e);

        if (this._pendingOp) {
            this._executePendingOp(idx);
            return;
        }

        this._dragging = true;
        this._sliderDirty = false;
        this._rangeStart = idx;
        this._rangeEnd = idx;
        this._grid.setPointerCapture(e.pointerId);
        this._updateRangeHighlight();
        this._updateRangePreview();
        this._syncPicker();
    }

    _onGridPointerMove(e) {
        if (!this._dragging) return;
        const idx = this._hitTestGrid(e);
        this._rangeEnd = idx;
        this._updateRangeHighlight();
        this._updateRangePreview();
    }

    _onGridPointerUp(e) {
        if (!this._dragging) return;
        this._dragging = false;
        this._grid.releasePointerCapture(e.pointerId);
        // Pick mode: single click sets color immediately
        if (this.onPick && this._rangeStart === this._rangeEnd) {
            this.onPick(this._rangeStart);
        }
        if (this._rangeStart > this._rangeEnd) {
            const tmp = this._rangeStart;
            this._rangeStart = this._rangeEnd;
            this._rangeEnd = tmp;
        }
        this._updateRangeHighlight();
        this._updateRangePreview();
        this._syncPicker();
    }

    _sortedRange() {
        const lo = Math.min(this._rangeStart, this._rangeEnd);
        const hi = Math.max(this._rangeStart, this._rangeEnd);
        return [lo, hi];
    }

    _updateRangeHighlight() {
        const [lo, hi] = this._sortedRange();
        for (let i = 0; i < 256; i++) {
            const sw = this._dlgSwatches[i];
            sw.classList.toggle('in-range', i >= lo && i <= hi);
            sw.classList.toggle('range-end', i === lo || i === hi);
        }
        this._updateRangeButtons();
    }

    _updateRangeButtons() {
        const [lo, hi] = this._sortedRange();
        const hasRange = hi > lo;
        // These need 2+ colors
        for (const b of [this._flipBtn, this._xflipBtn, this._spreadBtn, this._mergeBtn]) {
            if (b) b.disabled = !hasRange;
        }
        if (this._sortSel) this._sortSel.disabled = !hasRange;
    }

    _updateRangePreview() {
        const [lo, hi] = this._sortedRange();
        this._rangePreview.innerHTML = '';
        for (let i = lo; i <= hi; i++) {
            const strip = document.createElement('div');
            strip.className = 'range-color';
            const [r, g, b] = this.doc.palette.getColor(i);
            strip.style.backgroundColor = `rgb(${r},${g},${b})`;
            this._rangePreview.appendChild(strip);
        }
    }

    _syncPicker() {
        const [lo, hi] = this._sortedRange();
        const [r, g, b] = this.doc.palette.getColor(this._rangeStart);
        if (lo === hi) {
            this._indexLabel.textContent = `Index: ${lo}`;
        } else {
            this._indexLabel.textContent = `Index: ${lo}\u2013${hi} (${hi - lo + 1} colors)`;
        }
        // Only update HSV from palette if color changed externally;
        // otherwise keep precise HSV to avoid 6-bit snap round-trip drift
        let [er, eg, eb] = this._hsvToRgb(this._hue, this._sat, this._val);
        [er, eg, eb] = this._snapIf6bit(er, eg, eb);
        if (r !== er || g !== eg || b !== eb) {
            const [h, s, v] = this._rgbToHsv(r, g, b);
            if (s > 0) this._hue = h;
            this._sat = s;
            this._val = v;
        }
        this._syncRGBDisplay(r, g, b);
        // Re-render canvases and cursors
        this._renderSVCanvas();
        this._renderHueStrip();
        this._updateSVCursor();
        this._updateHueCursor();
    }

    updateSwatches() {
        for (let i = 0; i < 256; i++) {
            const [r, g, b] = this.doc.palette.getColor(i);
            this._dlgSwatches[i].style.backgroundColor = `rgb(${r},${g},${b})`;
        }
        this._updateRangePreview();
        this._syncPicker();
    }

    // ── 6-Bit Toggle ──────────────────────────────────────────────────

    _on6bitToggle(checkbox) {
        if (checkbox.checked) {
            if (!confirm('Convert current colors to 6-bit?')) {
                checkbox.checked = false;
                return;
            }
            this._pushPaletteHistory();
            for (let i = 0; i < 256; i++) {
                const [r, g, b] = this.doc.palette.getColor(i);
                this.doc.palette.setColor(i,
                    Math.round(r / 4) * 4,
                    Math.round(g / 4) * 4,
                    Math.round(b / 4) * 4
                );
            }
            this._6bit = true;
            this._updateInputRange(63);
            this.updateSwatches();
            this.bus.emit('palette-changed');
        } else {
            if (!confirm('Convert current colors to 8-bit?')) {
                checkbox.checked = true;
                return;
            }
            this._6bit = false;
            this._updateInputRange(255);
            this._syncPicker();
        }
    }

    _updateInputRange(max) {
        for (const s of [this._rSlider, this._gSlider, this._bSlider]) {
            s.max = max;
        }
        for (const n of [this._rNum, this._gNum, this._bNum]) {
            n.max = max;
        }
    }

    _snapIf6bit(r, g, b) {
        if (!this._6bit) return [r, g, b];
        return [
            Math.min(255, Math.round(r / 4) * 4),
            Math.min(255, Math.round(g / 4) * 4),
            Math.min(255, Math.round(b / 4) * 4)
        ];
    }

    // ── Simple Operations ─────────────────────────────────────────────

    _actionFlip() {
        const [lo, hi] = this._sortedRange();
        if (lo === hi) return;
        this._pushPaletteHistory();
        const pal = this.doc.palette;
        for (let i = 0; i < Math.floor((hi - lo + 1) / 2); i++) {
            const a = [...pal.getColor(lo + i)];
            const b = [...pal.getColor(hi - i)];
            pal.setColor(lo + i, ...b);
            pal.setColor(hi - i, ...a);
        }
        this.updateSwatches();
        this.bus.emit('palette-changed');
    }

    _actionXFlip() {
        const [lo, hi] = this._sortedRange();
        if (lo === hi) return;
        this._pushPaletteHistory();
        const mapping = new Array(256);
        for (let i = 0; i < 256; i++) mapping[i] = i;
        for (let i = 0; i <= hi - lo; i++) {
            mapping[lo + i] = hi - i;
        }
        this._actionFlip();
        this.doc.remapColorIndices(mapping);
        this.bus.emit('document-changed');
    }

    _actionInvert() {
        this._pushPaletteHistory();
        const [lo, hi] = this._sortedRange();
        const pal = this.doc.palette;
        for (let i = lo; i <= hi; i++) {
            const [r, g, b] = pal.getColor(i);
            pal.setColor(i, ...this._snapIf6bit(255 - r, 255 - g, 255 - b));
        }
        this.updateSwatches();
        this.bus.emit('palette-changed');
    }

    _actionGray() {
        this._pushPaletteHistory();
        const [lo, hi] = this._sortedRange();
        const pal = this.doc.palette;
        for (let i = lo; i <= hi; i++) {
            const [r, g, b] = pal.getColor(i);
            const avg = Math.round((r + g + b) / 3);
            const [sr, sg, sb] = this._snapIf6bit(avg, avg, avg);
            pal.setColor(i, sr, sg, sb);
        }
        this.updateSwatches();
        this.bus.emit('palette-changed');
    }

    _actionSpread() {
        const [lo, hi] = this._sortedRange();
        if (hi - lo < 2) return;
        this._pushPaletteHistory();
        const pal = this.doc.palette;
        const [r0, g0, b0] = pal.getColor(lo);
        const [r1, g1, b1] = pal.getColor(hi);
        const n = hi - lo;
        for (let i = 1; i < n; i++) {
            const t = i / n;
            const r = Math.round(r0 + (r1 - r0) * t);
            const g = Math.round(g0 + (g1 - g0) * t);
            const b = Math.round(b0 + (b1 - b0) * t);
            pal.setColor(lo + i, ...this._snapIf6bit(r, g, b));
        }
        this.updateSwatches();
        this.bus.emit('palette-changed');
    }

    _actionMerge() {
        const [lo, hi] = this._sortedRange();
        if (lo === hi) return;
        this._pushPaletteHistory();
        const pal = this.doc.palette;

        // Average all colors in the range
        let rSum = 0, gSum = 0, bSum = 0;
        const count = hi - lo + 1;
        for (let i = lo; i <= hi; i++) {
            const [r, g, b] = pal.getColor(i);
            rSum += r; gSum += g; bSum += b;
        }
        const [ar, ag, ab] = this._snapIf6bit(
            Math.round(rSum / count), Math.round(gSum / count), Math.round(bSum / count)
        );

        // Remap all pixels using indices [lo+1..hi] to lo
        const mapping = new Array(256);
        for (let i = 0; i < 256; i++) mapping[i] = i;
        for (let i = lo + 1; i <= hi; i++) mapping[i] = lo;
        this.doc.remapColorIndices(mapping);

        // Set merged color and zero out freed slots
        pal.setColor(lo, ar, ag, ab);
        for (let i = lo + 1; i <= hi; i++) {
            pal.setColor(i, 0, 0, 0);
        }

        this.updateSwatches();
        this.bus.emit('palette-changed');
        this.bus.emit('document-changed');
    }

    // ── Two-Step Operations ───────────────────────────────────────────

    _startPendingOp(type) {
        if (this._pendingOp && this._pendingOp.type === type) {
            this._cancelPendingOp();
            return;
        }
        this._cancelPendingOp();
        const [lo, hi] = this._sortedRange();
        const rangeLen = hi - lo + 1;
        this._pendingOp = { type, srcStart: lo, srcEnd: hi, rangeLen };
        this._grid.classList.add('pending-op');

        const labels = { swap: 'Swap', xswap: 'X-Swap' };
        this._statusEl.textContent = `Click destination for ${labels[type]} (${rangeLen} colors)`;
        this._statusEl.style.display = '';

        this._swapBtn.classList.toggle('active', type === 'swap');
        this._xswapBtn.classList.toggle('active', type === 'xswap');
    }

    _cancelPendingOp() {
        this._pendingOp = null;
        this._grid.classList.remove('pending-op');
        this._statusEl.style.display = 'none';
        this._swapBtn.classList.remove('active');
        this._xswapBtn.classList.remove('active');
    }

    _executePendingOp(destStart) {
        const op = this._pendingOp;
        if (!op) return;

        const len = op.rangeLen;
        const destEnd = destStart + len - 1;
        if (destStart < 0 || destEnd > 255) {
            this._cancelPendingOp();
            return;
        }

        const srcLo = op.srcStart, srcHi = op.srcEnd;
        const disjoint = destEnd < srcLo || destStart > srcHi;

        this._pushPaletteHistory();
        const pal = this.doc.palette;
        const mapping = new Array(256);
        for (let i = 0; i < 256; i++) mapping[i] = i;

        if (disjoint) {
            // Exact block swap: exchange the two equal-length ranges.
            for (let i = 0; i < len; i++) {
                const a = [...pal.getColor(srcLo + i)];
                const b = [...pal.getColor(destStart + i)];
                pal.setColor(srcLo + i, ...b);
                pal.setColor(destStart + i, ...a);
                mapping[srcLo + i] = destStart + i;
                mapping[destStart + i] = srcLo + i;
            }
        } else {
            // Overlapping drop: rotate the union range so the source block
            // slides onto the destination and the colors it passes over wrap
            // around to fill the vacated slots. A rotation is a bijection, so
            // the xswap remap still leaves the image visually identical.
            const uLo = Math.min(srcLo, destStart);
            const uHi = Math.max(srcHi, destEnd);
            const U = uHi - uLo + 1;
            const shift = destStart - srcLo;   // >0 shifts right, <0 shifts left
            const old = [];
            for (let i = 0; i < U; i++) old.push([...pal.getColor(uLo + i)]);
            for (let i = 0; i < U; i++) {
                const from = ((i - shift) % U + U) % U;
                pal.setColor(uLo + i, ...old[from]);
                mapping[uLo + from] = uLo + i;
            }
        }

        if (op.type === 'xswap') {
            this.doc.remapColorIndices(mapping);
            this.bus.emit('document-changed');
        }

        this._cancelPendingOp();
        this._rangeStart = destStart;
        this._rangeEnd = destEnd;
        this.updateSwatches();
        this._updateRangeHighlight();
        this.bus.emit('palette-changed');
    }

    // ── Copy / Paste Range ─────────────────────────────────────────────

    _actionCopyRange() {
        const [lo, hi] = this._sortedRange();
        const colors = [];
        const pal = this.doc.palette;
        for (let i = lo; i <= hi; i++) {
            colors.push([...pal.getColor(i)]);
        }
        PaletteEditDialog._rangeClipboard = colors;
        if (this._pasteRangeBtn) this._pasteRangeBtn.disabled = false;
    }

    _actionPasteRange() {
        const clip = PaletteEditDialog._rangeClipboard;
        if (!clip || clip.length === 0) return;
        const [lo] = this._sortedRange();
        if (lo + clip.length > 256) return;
        this._pushPaletteHistory();
        const pal = this.doc.palette;
        for (let i = 0; i < clip.length; i++) {
            pal.setColor(lo + i, ...clip[i]);
        }
        this._rangeEnd = lo + clip.length - 1;
        this._updateRangeHighlight();
        this.updateSwatches();
        this._syncPicker();
        this.bus.emit('palette-changed');
    }

    // ── Used / Zap Unused ─────────────────────────────────────────────

    _actionUsed() {
        this._usedHighlight = !this._usedHighlight;
        if (this._usedHighlight) {
            const used = this.doc.getUsedColorIndices();
            for (let i = 0; i < 256; i++) {
                this._dlgSwatches[i].classList.toggle('color-used', used.has(i));
            }
        } else {
            for (let i = 0; i < 256; i++) {
                this._dlgSwatches[i].classList.remove('color-used');
            }
        }
    }

    _actionZapUnused() {
        const used = this.doc.getUsedColorIndices();
        const usedCount = used.size;
        if (!confirm(`Reduce the colors to ${usedCount}?`)) return;
        this._pushPaletteHistory();

        const usedArr = [...used].sort((a, b) => a - b);
        const mapping = new Array(256).fill(0);
        const newColors = [];

        for (let i = 0; i < usedArr.length; i++) {
            mapping[usedArr[i]] = i;
            newColors.push([...this.doc.palette.getColor(usedArr[i])]);
        }

        while (newColors.length < 256) {
            newColors.push([0, 0, 0]);
        }

        for (let i = 0; i < 256; i++) {
            this.doc.palette.setColor(i, ...newColors[i]);
        }
        this.doc.remapColorIndices(mapping);
        this._rangeStart = 0;
        this._rangeEnd = Math.max(0, usedCount - 1);
        this.updateSwatches();
        this._updateRangeHighlight();
        this.bus.emit('palette-changed');
        this.bus.emit('document-changed');
    }

    // ── Sort ──────────────────────────────────────────────────────────

    _actionSort(mode) {
        const [lo, hi] = this._sortedRange();
        if (lo === hi) return;
        this._pushPaletteHistory();
        const pal = this.doc.palette;

        const entries = [];
        for (let i = lo; i <= hi; i++) {
            entries.push({ index: i, color: [...pal.getColor(i)] });
        }

        if (mode === 'hue') {
            entries.sort((a, b) => {
                const ha = this._rgbToHsl(a.color);
                const hb = this._rgbToHsl(b.color);
                return ha[0] - hb[0] || ha[2] - hb[2];
            });
        } else if (mode === 'light') {
            entries.sort((a, b) => {
                return (a.color[0] + a.color[1] + a.color[2]) -
                       (b.color[0] + b.color[1] + b.color[2]);
            });
        } else if (mode === 'hist') {
            const hist = this.doc.getColorHistogram();
            entries.sort((a, b) => hist[b.index] - hist[a.index]);
        }

        for (let i = 0; i < entries.length; i++) {
            pal.setColor(lo + i, ...entries[i].color);
        }

        // Sort always remaps pixels so the image looks unchanged — the color
        // originally at entries[i].index now lives at lo + i. Reordering the
        // palette without remapping would just corrupt the image, which we
        // never want.
        const mapping = new Array(256);
        for (let i = 0; i < 256; i++) mapping[i] = i;
        for (let i = 0; i < entries.length; i++) {
            mapping[entries[i].index] = lo + i;
        }
        this.doc.remapColorIndices(mapping);
        this.bus.emit('document-changed');

        this.updateSwatches();
        this.bus.emit('palette-changed');
    }

    _rgbToHsl([r, g, b]) {
        r /= 255; g /= 255; b /= 255;
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        const l = (max + min) / 2;
        if (max === min) return [0, 0, l];
        const d = max - min;
        const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        let h;
        if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        else if (max === g) h = ((b - r) / d + 2) / 6;
        else h = ((r - g) / d + 4) / 6;
        return [h, s, l];
    }

    // ── Reduce ────────────────────────────────────────────────────────

    _actionReduce(n) {
        if (n < 1 || n > 256) return;
        this._pushPaletteHistory();
        const pal = this.doc.palette;
        const hist = this.doc.getColorHistogram();
        const used = this.doc.getUsedColorIndices();

        // Collect all distinct non-black palette entries (or used ones)
        const colorSet = new Set();
        const colors = [];
        for (let i = 0; i < 256; i++) {
            const [r, g, b] = pal.getColor(i);
            const key = (r << 16) | (g << 8) | b;
            if (!colorSet.has(key) || used.has(i)) {
                colors.push({ index: i, color: [r, g, b], count: hist[i] || 0 });
                colorSet.add(key);
            }
        }

        if (colors.length <= n) return;

        const representatives = this._medianCut(colors, n);

        const newColors = representatives.map(rep => rep.color);

        // Map every palette index to nearest representative
        const mapping = new Array(256);
        for (let i = 0; i < 256; i++) {
            const [r, g, b] = pal.getColor(i);
            let bestDist = Infinity, bestI = 0;
            for (let j = 0; j < newColors.length; j++) {
                const [cr, cg, cb] = newColors[j];
                const dist = (r - cr) ** 2 + (g - cg) ** 2 + (b - cb) ** 2;
                if (dist < bestDist) { bestDist = dist; bestI = j; }
            }
            mapping[i] = bestI;
        }

        // Write new palette: N colors at front, rest black
        for (let i = 0; i < 256; i++) {
            if (i < newColors.length) {
                pal.setColor(i, ...this._snapIf6bit(...newColors[i]));
            } else {
                pal.setColor(i, 0, 0, 0);
            }
        }

        this.doc.remapColorIndices(mapping);
        this._rangeStart = 0;
        this._rangeEnd = Math.max(0, newColors.length - 1);
        this.updateSwatches();
        this._updateRangeHighlight();
        this.bus.emit('palette-changed');
        this.bus.emit('document-changed');
    }

    _medianCut(colors, n) {
        if (colors.length <= n) return colors;

        let buckets = [colors];

        while (buckets.length < n) {
            let bestBucket = 0, bestRange = -1, bestChannel = 0;
            for (let bi = 0; bi < buckets.length; bi++) {
                const bucket = buckets[bi];
                if (bucket.length < 2) continue;
                for (let ch = 0; ch < 3; ch++) {
                    let min = 255, max = 0;
                    for (const c of bucket) {
                        if (c.color[ch] < min) min = c.color[ch];
                        if (c.color[ch] > max) max = c.color[ch];
                    }
                    if (max - min > bestRange) {
                        bestRange = max - min;
                        bestBucket = bi;
                        bestChannel = ch;
                    }
                }
            }

            if (bestRange <= 0) break;

            const bucket = buckets[bestBucket];
            bucket.sort((a, b) => a.color[bestChannel] - b.color[bestChannel]);
            const mid = Math.floor(bucket.length / 2);
            buckets.splice(bestBucket, 1, bucket.slice(0, mid), bucket.slice(mid));
        }

        return buckets.map(bucket => {
            let totalW = 0, rSum = 0, gSum = 0, bSum = 0;
            for (const c of bucket) {
                const w = c.count || 1;
                totalW += w;
                rSum += c.color[0] * w;
                gSum += c.color[1] * w;
                bSum += c.color[2] * w;
            }
            return {
                color: [
                    Math.round(rSum / totalW),
                    Math.round(gSum / totalW),
                    Math.round(bSum / totalW)
                ]
            };
        });
    }

    // ── Palette History ────────────────────────────────────────────────

    _pushPaletteHistory() {
        // Snapshot the palette AND pixel data. Palette ops such as X-Swap / Sort
        // remap pixel indices in place (doc.remapColorIndices), so restoring the
        // palette alone would leave the pixels remapped and corrupt the image.
        // Mirror the clone strategy used by open() / _ok() / _cancel().
        this._paletteHistory.push({
            palette: this.doc.palette.export(),
            layers: this.doc.layers.map(l => l.clone(true)),
            anim: this.doc.snapshotAnimation(),
        });
        if (this._undoBtn) this._undoBtn.disabled = false;
    }

    _undoPalette() {
        if (this._paletteHistory.length === 0) return;
        const prev = this._paletteHistory.pop();
        this.doc.palette.import(prev.palette);
        this.doc.layers = prev.layers;
        if (prev.anim) {
            this.doc.restoreAnimation(prev.anim);
            this.doc.loadFrame(this.doc.activeFrameIndex);
        }
        this.updateSwatches();
        this.bus.emit('palette-changed');
        this.bus.emit('document-changed');
        if (this._undoBtn) this._undoBtn.disabled = this._paletteHistory.length === 0;
    }

    // ── Load / Save Palette ──────────────────────────────────────────

    _loadPalette() {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pal,.bmp,.pcx';
        input.addEventListener('change', () => {
            if (!input.files[0]) return;
            const file = input.files[0];
            const reader = new FileReader();
            reader.onload = () => {
                const bytes = new Uint8Array(reader.result);
                let colors = null;
                const ext = file.name.split('.').pop().toLowerCase();
                if (ext === 'pal') {
                    colors = importPAL(bytes);
                } else if (ext === 'bmp') {
                    try {
                        const doc = importBMP(reader.result);
                        colors = doc.palette.export();
                    } catch (e) { /* ignore */ }
                } else if (ext === 'pcx') {
                    try {
                        const doc = importPCX(reader.result);
                        colors = doc.palette.export();
                    } catch (e) { /* ignore */ }
                }
                if (colors) {
                    this._pushPaletteHistory();
                    this.doc.palette.import(colors);
                    this.updateSwatches();
                    this.bus.emit('palette-changed');
                }
            };
            reader.readAsArrayBuffer(file);
        });
        input.click();
    }

    _savePalette() {
        const blob = exportPAL(this.doc.palette, this._6bit);
        downloadBlob(blob, 'palette.pal');
    }

    // ── OK / Cancel ───────────────────────────────────────────────────

    _ok() {
        // Push palette change to document undo history
        if (this.doc.animationEnabled) this.doc.saveCurrentFrame();
        const afterPalette = this.doc.palette.export();
        const afterLayers = this.doc.layers.map(l => l.clone(true));
        const afterAnim = this.doc.snapshotAnimation();
        // Check if anything actually changed
        let changed = false;
        for (let i = 0; i < 256; i++) {
            const [r1, g1, b1] = this._originalPalette[i];
            const [r2, g2, b2] = afterPalette[i];
            if (r1 !== r2 || g1 !== g2 || b1 !== b2) { changed = true; break; }
        }
        if (!changed && this._originalLayers.length === this.doc.layers.length) {
            for (let i = 0; i < this.doc.layers.length && !changed; i++) {
                const a = this._originalLayers[i].data;
                const b = this.doc.layers[i].data;
                if (a.length !== b.length) { changed = true; break; }
                for (let j = 0; j < a.length; j++) {
                    if (a[j] !== b[j]) { changed = true; break; }
                }
            }
        }
        if (changed && this.undoManager) {
            this.undoManager.undoStack.push({
                type: 'palette',
                beforePalette: this._originalPalette,
                afterPalette: afterPalette,
                beforeLayers: this._originalLayers,
                afterLayers: afterLayers,
                beforeAnim: this._originalAnim,
                afterAnim: afterAnim,
            });
            this.undoManager.redoStack = [];
        }
        this._destroy();
    }

    _cancel() {
        this.doc.palette.import(this._originalPalette);
        this.doc.layers = this._originalLayers;
        if (this._originalAnim) {
            this.doc.restoreAnimation(this._originalAnim);
            this.doc.loadFrame(this.doc.activeFrameIndex);
        }
        this.bus.emit('palette-changed');
        this.bus.emit('document-changed');
        this._destroy();
    }

    _destroy() {
        document.removeEventListener('keydown', this._onKey);
        // See Dialog.close(): blur before detaching so focus doesn't get wedged.
        const active = document.activeElement;
        if (active && active.blur && this._overlay.contains(active)) active.blur();
        this._overlay.remove();
        this.onClose?.();
    }
}

PaletteEditDialog._rangeClipboard = null;
